import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { InteractivePlayer } from '../components/InteractivePlayer'
import { AppFullscreenShell } from '../components/AppFullscreenShell'
import { VideoActionBar, VideoReportDialog } from '../components/VideoSocial'
import { useAuth } from '../contexts/AuthContext'
import { api, ApiError } from '../lib/api'
import { normalizeProjectConfig, type ProjectDetail } from '../lib/project'

export function SharePage() {
  const { shareSlug = '' } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [project, setProject] = useState<ProjectDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionMessage, setActionMessage] = useState('')
  const [reportOpen, setReportOpen] = useState(false)
  const [reportLoginPrompt, setReportLoginPrompt] = useState(false)
  const reportTrigger = useRef<HTMLButtonElement>(null)
  const [fullscreen, setFullscreen] = useState(false)
  const fullscreenTrigger = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    void api<ProjectDetail>(`/api/share/${encodeURIComponent(shareSlug)}`)
      .then((data) => {
        if (active) setProject({ ...data, config: normalizeProjectConfig(data.config) })
      })
      .catch((caught: ApiError) => {
        if (active) setError(caught.message)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [shareSlug])

  if (loading) return <div className="page-status" role="status">正在載入分享作品…</div>
  if (!project || error) {
    const unavailable = error === '這支影片目前無法在 iLoveSing 使用。'
    return (
      <div className="page narrow-page">
        <div className="empty-state" role="alert">
          <h1>{unavailable ? '影片目前無法播放' : '作品無法開啟'}</h1>
          <p>{unavailable ? '這支影片目前無法在 iLoveSing 使用。' : error || '此分享連結不存在或已停止分享。'}</p>
          <button className="button button--primary" type="button" onClick={() => location.state?.fromExplore ? navigate(-1) : navigate('/explore')}>
            {location.state?.fromExplore ? '返回探索' : '回到公開作品'}
          </button>
        </div>
      </div>
    )
  }

  const fork = async () => {
    setActionMessage('')
    if (!user) {
      navigate('/login', { state: { from: window.location.pathname } })
      return
    }
    try {
      const data = await api<{ id: string }>(`/api/projects/${project.id}/fork`, {
        method: 'POST',
        body: JSON.stringify({}),
      })
      navigate(`/projects/${data.id}/edit`)
    } catch (caught) {
      setActionMessage(caught instanceof ApiError ? caught.message : '目前無法複製作品')
    }
  }

  return (
    <div className="page share-page">
      <section className="share-stage" aria-labelledby="share-title">
        <header className="share-header">
          <div>
            {location.state?.fromExplore && <button className="button share-back-link" type="button" onClick={() => navigate(-1)}>← 返回探索</button>}
            <span className="eyebrow">公開作品</span>
            <h1 id="share-title">{project.youtubeTitle || project.title}</h1>
            <div className="byline">
              {project.youtubeChannelTitle && (
                <span>YouTube 頻道：<strong>{project.youtubeChannelTitle}</strong></span>
              )}
              <span>編排：<strong>{project.creatorName ?? '匿名使用者'}</strong></span>
              {project.sourceTitle && <span>來源：{project.sourceTitle}</span>}
            </div>
          </div>
          <div className="button-row">
            <button className="button" type="button" onClick={fork}>
              複製成我的版本
            </button>
            <button
              className="button button--quiet"
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(window.location.href).then(() => setActionMessage('分享網址已複製'))
              }}
            >
              複製連結
            </button>
          </div>
          <VideoActionBar videoId={project.youtubeVideoId} sourceProjectId={project.id} initialFavoriteCount={project.favoriteCount} initialFavorited={project.isFavorited} />
        </header>
        {actionMessage && <div className="alert" role="status">{actionMessage}</div>}
        <AppFullscreenShell open={fullscreen} title={project.youtubeTitle || project.title} triggerRef={fullscreenTrigger} onOpen={() => setFullscreen(true)} onClose={() => setFullscreen(false)}>
          {(beginOpen, immersiveActive) => <InteractivePlayer config={project.config} showNextChoices shareLayout immersiveEnabled immersiveActive={immersiveActive} onImmersiveOpen={beginOpen} onActualPlay={(videoId, eventId) => { void api(`/api/videos/${videoId}/play-entries`, { method: 'POST', body: JSON.stringify({ eventId }) }).catch(() => undefined) }} />}
        </AppFullscreenShell>
        {project.description && (
          <section className="about-work">
            <h2>關於這個作品</h2>
            <p>{project.description}</p>
          </section>
        )}
      </section>
      <section className="report-panel">
        <div>
          <h2>這個作品有問題？</h2>
          <p>本站可處理作品設定與公開狀態，但無法刪除 YouTube 原影片。</p>
        </div>
        <div className="button-row">
          <button ref={reportTrigger} className="button" type="button" onClick={() => user ? setReportOpen(true) : setReportLoginPrompt(true)}>{user ? '檢舉這支影片' : '登入後檢舉'}</button>
          <a
            className="button"
            href={`https://www.youtube.com/watch?v=${project.youtubeVideoId}`}
            target="_blank"
            rel="noreferrer"
          >
            前往 YouTube
          </a>
        </div>
      </section>
      {reportOpen && <VideoReportDialog videoId={project.youtubeVideoId} title={project.youtubeTitle || project.title} channelTitle={project.youtubeChannelTitle} triggerRef={reportTrigger} onClose={() => setReportOpen(false)} />}
      {reportLoginPrompt && <div className="modal-backdrop"><section className="modal" role="dialog" aria-modal="true" aria-labelledby="report-login-title"><h2 id="report-login-title">請登入後再檢舉影片。</h2><p>登入後才能送出檢舉，並在我的檢舉查看處理進度。</p><div className="button-row modal-actions"><button className="button" type="button" onClick={() => setReportLoginPrompt(false)}>取消</button><button className="button button--primary" type="button" onClick={() => navigate('/login', { state: { from: window.location.pathname } })}>登入後繼續</button></div></section></div>}
    </div>
  )
}
